import { GridColDef } from "@mui/x-data-grid";
import { useDocumentosSetoriaisDispatch, useDocumentosSetoriaisState } from "./documentos-setoriais-context";
import { ACTION_TYPES, initialDataRequest } from "./documentos-setoriais-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { IDocumentoSetorialResponse } from "../../../../model/admin-portal/response/documento-setorial-response";
import { ITermPaggedRequest } from "../../../../model/admin-portal/request/term-pagged-request";
import { IconButton, Box, Chip, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { IAnexoDocumentoSetorialRequest } from "../../../../model/admin-portal/request/anexo-documento-setorial-request";

// Formata o período de forma legível conforme a periodicidade do tipo de documento selecionado
// (2=Bimestral, 3=Quadrimestral mostram o número do período; 1=Anual, 4=Quadrienal e 5=Trimestral mostram só o(s) ano(s) - exceto trimestre, que também tem número).
export const formatPeriodo = (indPeriodicidade: number, ano: number, periodo?: number | null): string => {
    switch (indPeriodicidade) {
        case 2: return periodo ? `${periodo}º Bimestre/${ano}` : `${ano}`;
        case 3: return periodo ? `${periodo}º Quadrimestre/${ano}` : `${ano}`;
        case 4: return `${ano}-${ano + 3}`;
        case 5: return periodo ? `${periodo}º Trimestre/${ano}` : `${ano}`;
        default: return `${ano}`;
    }
};

export const useDocumentosSetoriaisController = () => {
    const state = useDocumentosSetoriaisState();
    const dispatch = useDocumentosSetoriaisDispatch();
    const { showMessage, showBackDrop } = useBaseLayoutController();

    const loadData = async (searchRequest: ITermPaggedRequest) => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listDocumentosSetoriais(searchRequest);
            dispatch({ type: ACTION_TYPES.setDataPaginatedListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar documentos setoriais' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        const newSearch = { ...state.dataSearchRequest, page, pageSize };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const handleOnChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: { ...state.dataSearchRequest, term: e.target.value } });
    };

    const clearFields = async () => {
        const newSearch = { page: 0, pageSize: state.dataSearchRequest.pageSize, term: '' };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const loadLookups = async () => {
        try {
            const tipos = await AdminPortalAppService.listTiposDocumentoSetorial();
            dispatch({ type: ACTION_TYPES.setTiposList, payload: tipos });
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar lista de Tipos de Documento' });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getDocumentoSetorial(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });
            dispatch({
                type: ACTION_TYPES.setDataRequest, payload: {
                    id: response.id,
                    idTipoDocumentoSetorial: response.idTipoDocumentoSetorial,
                    ano: response.ano,
                    periodo: response.periodo ?? null,
                    textoDescricao: response.textoDescricao,
                    indStatus: response.indStatus,
                    dtaPublicacao: response.dtaPublicacao,
                    anexos: response.anexos?.map(d => ({ id: d.id, descricao: d.descricao, dtaPublicacao: d.dtaPublicacao, documentoUrl: d.documentoUrl })) || []
                }
            });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar documento setorial' });
        } finally {
            showBackDrop(false);
        }
    };

    const getTipoSelecionado = () => state.tiposList?.find(t => t.id === state.dataRequest?.idTipoDocumentoSetorial);

    const validFields = (): boolean => {
        const r = state.dataRequest;
        if (!r.idTipoDocumentoSetorial) return false;
        if (!r.ano || r.ano <= 0) return false;
        if (!r.textoDescricao || r.textoDescricao.trim() === '') return false;
        if (r.indStatus === null || r.indStatus === undefined) return false;
        if (!r.dtaPublicacao) return false;

        const tipo = getTipoSelecionado();
        if (tipo && (tipo.indPeriodicidade === 2 || tipo.indPeriodicidade === 3 || tipo.indPeriodicidade === 5) && !r.periodo) return false;

        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        let updated = { ...state.dataRequest, [field]: value };

        // Trocar o tipo de documento pode mudar a periodicidade - zera o período selecionado
        // para não deixar um valor de bimestre/quadrimestre/trimestre inconsistente com o novo tipo.
        if (field === 'idTipoDocumentoSetorial') {
            updated.periodo = null;
        }

        dispatch({ type: ACTION_TYPES.setDataRequest, payload: updated });
    };

    const openForm = (row?: IDocumentoSetorialResponse) => {
        if (row) {
            getData(row.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateDocumentoSetorial(state.dataRequest);
            showMessage({
                type: 'success',
                message: state.dataRequest.id !== undefined && state.dataRequest.id !== null ? 'Documento setorial atualizado com sucesso' : 'Documento setorial criado com sucesso'
            });
            closeForm();
            await loadData(state.dataSearchRequest);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar documento setorial' });
        } finally {
            showBackDrop(false);
        }
    };

    //Cada anexo é mantido só no estado local até o "Salvar" do Documento Setorial inteiro - linhas novas usam id 0
    const saveAnexo = (anexo: IAnexoDocumentoSetorialRequest, index?: number) => {
        const anexos = [...state.dataRequest.anexos];
        if (index !== undefined) anexos[index] = anexo; else anexos.push(anexo);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, anexos } });
    };

    const removeAnexo = (index: number) => {
        const anexos = state.dataRequest.anexos.filter((_, i) => i !== index);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, anexos } });
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            { field: 'areaName', headerName: 'Área', width: 150 },
            { field: 'tipoDocumentoSetorialName', headerName: 'Tipo de Documento', flex: 1.4, minWidth: 220 },
            {
                field: 'periodo', headerName: 'Período', width: 150,
                valueGetter: (_value, row) => formatPeriodo(row.indPeriodicidade, row.ano, row.periodo)
            },
            {
                field: 'indStatus', headerName: 'Status', width: 130,
                renderCell: (params) => (
                    <Chip
                        label={params.value === 2 ? 'Finalizado' : 'Em Execução'}
                        color={params.value === 2 ? 'success' : 'warning'}
                        size="small"
                    />
                )
            },
            { field: 'textoDescricao', headerName: 'Descrição', flex: 1.6, minWidth: 220 },
            {
                field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openForm(params.row); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    };

    return {
        ...state,
        loadData,
        onChangePagination,
        handleOnChangeSearch,
        clearFields,
        loadLookups,
        getData,
        getTipoSelecionado,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        save,
        getColumnsGrid,
        saveAnexo,
        removeAnexo
    };
};
